import React, { useEffect } from 'react';
import Script from 'next/script';
import styled from 'styled-components';
import BlockWrapper from './BlockWrapper';
import Container from '../interface/Container';

const Title = styled.h2`
  font-family: var(--font-tertiary);
  font-weight: var(--font-weight-regular);
  font-size: 3.8rem;
  margin-bottom: 4rem;
`;

const WidgetWrapper = styled.div`
  min-height: 500px;
`;

const WidgetContainer = styled.div`
  /* Override Bsport CSS variables for .bs-setup-variable elements */
  .bs-setup-variable,
  .bs-setup-variable * {
    ${({ customVariables }) =>
      customVariables &&
      Object.entries(customVariables)
        .map(([key, value]) => `--${key}: ${value};`)
        .join('\n    ')}
  }
`;

const BsportCalendarWidget = ({ block }) => {
  // Parse widget configuration from JSON
  const getWidgetConfig = () => {
    // Default configuration
    const defaultConfig = {
      parentElement: 'bsport-widget-280480',
      companyId: 2917,
      franchiseId: null,
      dialogMode: 1,
      widgetType: 'calendar',
      showFab: false,
      fullScreenPopup: false,
      styles: undefined,
      config: {
        calendar: { todayOnly: false, compactMode: null, metaActivities: [129080, 129120, 129121, 129146, 206918] },
      },
    };

    // If widgetConfig is provided, parse and merge with defaults
    if (block.widgetConfig) {
      try {
        // Sanitize the JSON string by replacing undefined with null
        const sanitizedConfig = block.widgetConfig.replace(/:\s*undefined(\s*[,}])/g, ': null$1');
        const customConfig = JSON.parse(sanitizedConfig);
        return { ...defaultConfig, ...customConfig };
      } catch (error) {
        console.error('Invalid widget config JSON:', error);
        return defaultConfig;
      }
    }

    return defaultConfig;
  };

  const widgetConfig = getWidgetConfig();

  // Default CSS variable overrides
  const defaultCssVariables = {
    'subtitle1-fontSize': '1.6rem',
    'h4-fontSize': '2rem',
    'h5-fontSize': '1.6rem',
    'h6-fontSize': '1.6rem',
    'body2-fontSize:': '1.4rem',
    'body1-fontSize:': '1.4rem',
    'button-fontSize': '1.4rem',
  };

  // Extract CSS variable overrides from widgetConfig if provided, merge with defaults
  const cssVariables = widgetConfig.cssVariables
    ? { ...defaultCssVariables, ...widgetConfig.cssVariables }
    : defaultCssVariables;

  useEffect(() => {
    const mountWidget = (config, repeat = 1) => {
      if (repeat > 50) return;

      if (!window.BsportWidget) {
        return setTimeout(() => {
          mountWidget(config, repeat + 1);
        }, 100 * repeat || 1);
      }

      window.BsportWidget.mount(config);
    };

    mountWidget(widgetConfig);
  }, [widgetConfig]);

  return (
    <BlockWrapper>
      <Container size="large">
        {block.mainTitle && <Title>{block.mainTitle}</Title>}

        <Script id="bsport-widget-cdn" src="https://cdn.bsport.io/scripts/widget.js" strategy="afterInteractive" />
        <WidgetContainer customVariables={cssVariables}>
          <WidgetWrapper id={widgetConfig.parentElement} />
        </WidgetContainer>
      </Container>
    </BlockWrapper>
  );
};

export default BsportCalendarWidget;
